import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";

import { isModelNodeInLockedBlock } from "@/components/tiptap/lock/utils";
import { afterDelayedFocus } from "@/utils/afterDelayedFocus";
import { getIsMobile } from "@/utils/getIsMobile";
import { createLogger } from "@/utils/logger";

import type { ExpressionNodeModel } from "./nodes/ExpressionNodeModel";
import type { SuggestionProviderType } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { RefObject } from "react";

const logger = createLogger("InputModel");

type InitCaretPosition = null | { from: number; to: number } | { left: number };

const SMOOTH_SCROLL_OPTIONS: ScrollIntoViewOptions = { behavior: "smooth", block: "center" };

export class InputModel {
  doc: DocumentModel;

  value: string = "";

  private inputRef: null | RefObject<HTMLInputElement | null> = null;

  private _editingNode?: ExpressionNodeModel = undefined;

  private _inputPosRange = { from: -1, to: -1 };

  private _initCaretPosition: InitCaretPosition = null;

  private queuedValueOnMathFocus?: string;

  smoothScrollRequested = false;

  constructor(doc: DocumentModel) {
    this.doc = doc;
    makeAutoObservable(this, {
      inputPosRange: false,
      inputSelection: false,
    });
  }

  get initCaretPosition(): InitCaretPosition {
    return this._initCaretPosition;
  }

  resetInitCaretPosition(): void {
    this._initCaretPosition = null;
  }

  get isFocused(): boolean {
    return this.inputRef?.current === document.activeElement;
  }

  isEditing = computedFn((node?: NodeModel): boolean => {
    return !!node && node === this.editingNode;
  });

  editNode(node: NodeModel, selected: "all" | number | { left: number } = "all"): void {
    if (node.isEditable && !isModelNodeInLockedBlock(node)) {
      this.setValueFromNode(node);
      this._editingNode = node;
      this.doc.editor.suggestions.mode = node.isInlineEditable() ? "inline" : "active";

      if (selected === "all") {
        this._initCaretPosition = { from: 0, to: this.value.length };
      } else if (typeof selected === "number") {
        this._initCaretPosition = { from: selected, to: selected };
      } else {
        this._initCaretPosition = selected;
      }
    } else {
      this._initCaretPosition = null;
      this._editingNode = undefined;
    }
  }

  get editingNode(): ExpressionNodeModel | undefined {
    return this._editingNode;
  }

  setValue(value: string) {
    this.value = value;
  }

  private setValueFromNode(node: ExpressionNodeModel | undefined) {
    if (!node) {
      return;
    }

    switch (node.type) {
      case "operator": {
        this.setValue(node.operatorSymbol);
        break;
      }

      case "variable":
      case "number":
      case "constant":
      case "function":
      case "operatorSymbol": {
        if (node.value) {
          this.setValue(node.value);
        }
        break;
      }

      default: {
        break;
      }
    }
  }

  reset(resetNode = false) {
    this.value = "";

    if (resetNode) {
      this._editingNode = undefined;
    }
  }

  get suggestionProviderTypes(): SuggestionProviderType[] {
    return [];
  }

  registerInputRef(ref: RefObject<HTMLInputElement | null>) {
    this.inputRef = ref;
  }

  unregisterInputRef() {
    this.inputRef = null;
  }

  /**
   * `caretAtEnd` is for a freshly mounted input only: a refocused one keeps the caret the user left.
   */
  focus({ caretAtEnd = false }: { caretAtEnd?: boolean } = {}) {
    if (this.doc.isInTransaction) {
      logger.warn(
        "Trying to focus input while document is in transaction. This can cause current transaction to be omitted from undo stack.",
      );
    }
    this.inputRef?.current?.focus({ preventScroll: this.smoothScrollRequested });
    if (caretAtEnd) {
      const len = this.value.length;
      this.inputRef?.current?.setSelectionRange(len, len);
    }
  }

  /**
   * Scrolls the input smoothly into view once the cursor has settled, suppressing the browser's
   * instant scroll from any focus() in between.
   *
   * @example
   * doc.input.requestSmoothScroll();
   * doc.cursor.moveTo(inlineNode, inlineNode.nodes.length);
   */
  requestSmoothScroll(options: ScrollIntoViewOptions = SMOOTH_SCROLL_OPTIONS) {
    this.smoothScrollRequested = true;

    afterDelayedFocus(() => {
      this.smoothScrollRequested = false;

      // Measured only now: before the cursor settles, the input may still be the old one.
      const target = this.inputElement ?? this.textCaretElement;
      const rect = target?.getBoundingClientRect();
      if (rect && (rect.top < 0 || rect.bottom > window.innerHeight)) {
        target?.scrollIntoView(options);
      }
    });
  }

  private get textCaretElement(): Element | null {
    const view = this.doc.editor.tiptap?.view;
    if (!view) return null;

    const { node } = view.domAtPos(view.state.selection.from);
    return node instanceof Element ? node : node.parentElement;
  }

  get inputSelection() {
    const input = this.inputRef?.current;
    return input ? { from: input.selectionStart ?? 0, to: input.selectionEnd ?? 0 } : null;
  }

  get inputElement(): HTMLInputElement | null {
    return this.inputRef?.current ?? null;
  }

  get inputPosRange() {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return { from: -1, to: -1 };

    return this._inputPosRange;
  }

  setInputPosRange(range: { from: number; to: number }, force?: boolean) {
    const { tiptap } = this.doc.editor;
    if (!tiptap) return;

    const prevRange = this._inputPosRange;
    this._inputPosRange = range;

    if (force || range.from !== prevRange.from || range.to !== prevRange.to) {
      tiptap.commands.setInputPosRange(range);
    }
  }

  queueValueOnMathFocus(value: string) {
    this.queuedValueOnMathFocus = value;
  }

  /** Applies a value queued by `queueValueOnMathFocus`; returns whether there was one. */
  applyQueuedValueOnMathFocus(): boolean {
    if (!this.queuedValueOnMathFocus) {
      return false;
    }
    this.value = this.queuedValueOnMathFocus;
    this.doc.editor.suggestions.mode = "active";
    this.queuedValueOnMathFocus = undefined;
    return true;
  }

  updateValueAfterFocus() {
    if (this.applyQueuedValueOnMathFocus() || this._editingNode) {
      return;
    }
    // On mobile only the native bridge calls this, after inserting a toolbar symbol; the editor
    // blur may have applied the queue already, and a reset would wipe that symbol.
    if (!getIsMobile()) {
      this.reset();
    }
  }
}
